import { useRef } from "react";
import { RnArrowRight } from "@remi/icons/next";
import { RemiButton, useEffect } from "@remi/next-ui";

import type { SelectedSecondFactor } from "~/utils/mfa";
import { factorKey, secondFactorLabel } from "~/utils/mfa";
import { formatMaskedPhoneIdentifier } from "./format-phone";

type MfaChallengePickerProps = {
  options: SelectedSecondFactor[];
  onSelect: (factor: SelectedSecondFactor) => void;
};

const labelForFactor = (factor: SelectedSecondFactor): string => {
  if (factor.strategy === "phone_code") {
    return `Text a code to ${formatMaskedPhoneIdentifier(factor.safeIdentifier)}`;
  }
  return secondFactorLabel(factor.strategy);
};

export const MfaChallengePicker = ({ options, onSelect }: MfaChallengePickerProps) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="flex flex-col gap-6" data-testid="mfa-challenge-picker">
      <h2 ref={headingRef} tabIndex={-1} className="font-semibold text-lg text-text-primary">
        Select a method to verify your identity
      </h2>
      <ul className="flex flex-col gap-2">
        {options.map((factor) => (
          <li key={factorKey(factor)}>
            <RemiButton
              variant="filled"
              size="lg"
              fullWidth
              onClick={() => onSelect(factor)}
              endIcon={<RnArrowRight size="sm" className="text-text-secondary" aria-hidden="true" />}
              className="justify-between rounded-lg"
              childrenClassName="flex-1 truncate text-left"
              data-testid={`mfa-picker-option-${factor.strategy}`}
            >
              {labelForFactor(factor)}
            </RemiButton>
          </li>
        ))}
      </ul>
    </div>
  );
};
